import { useState } from 'react'
import { Info, SlidersHorizontal } from 'lucide-react'
import { SKILL_TIERS, getSkillTierIndex } from '../../skillTiers'
import {
  HOME_PICK_BASELINE_PCT,
  scaleToSkillMeterBar,
} from '../../skillMeterScale'
import {
  CALIBRATION_GAMEWEEKS,
  type CalibrationState,
} from '../../calibration'
import type { SeasonStats } from '../../types'
import SkillMeterInfoModal from '../SkillMeterInfoModal'
import { formatPct } from './formatPct'
import { useSkillMeterMotion } from './useSkillMeterMotion'
import './SkillMeter.css'

const SKILL_METER_FILL_DURATION_MS = 1300

function SkillMeter({
  seasonStats,
  profileKey,
  calibration,
  isSelf,
}: {
  seasonStats: SeasonStats
  profileKey: string
  calibration: CalibrationState
  /** The unlock reveal is a one-time personal moment — it never plays while
   * looking at someone else's profile. */
  isSelf: boolean
}) {
  const pct = seasonStats.correctOutcomePct ?? 0
  const tierIndex = getSkillTierIndex(pct)
  const tier = SKILL_TIERS[tierIndex]
  const nextTier = SKILL_TIERS[tierIndex + 1] ?? null
  const fillPct = scaleToSkillMeterBar(pct)
  // The "always pick the home team" reference the pin marks: a fixed benchmark,
  // identical on every profile and in every state of the season.
  const baselineFillPct = scaleToSkillMeterBar(HOME_PICK_BASELINE_PCT)
  const TierIcon = tier.icon

  const { isFillRevealed, isPulsing, revealedProgress, unlockPhase, showCalibrating } =
    useSkillMeterMotion({ pct, tierIndex, profileKey, calibration, isSelf })
  const [isInfoOpen, setIsInfoOpen] = useState(false)

  return (
    <div
      className={`profile__skill-meter ${
        isPulsing ? 'profile__skill-meter--pulse' : ''
      } ${showCalibrating ? 'profile__skill-meter--calibrating' : ''} ${
        unlockPhase === 'revealing' ? 'profile__skill-meter--unlocking' : ''
      }`}
      style={
        showCalibrating
          ? undefined
          : ({
              '--tier-color': tier.color,
              '--tier-ink-color': tier.inkColor,
              '--tier-text-color': tier.textColor,
            } as React.CSSProperties)
      }
    >
      <button
        type="button"
        className="profile__skill-meter-label label-plain"
        onClick={() => setIsInfoOpen(true)}
        aria-label="Intuition Rating — tap to learn how this stat and the skill tiers work"
      >
        Intuition Rating
        <Info className="profile__skill-meter-label-icon" aria-hidden="true" />
      </button>

      {showCalibrating ? (
        <>
          <div className="profile__skill-meter-headline">
            <button
              type="button"
              className="profile__skill-meter-tier"
              onClick={() => setIsInfoOpen(true)}
              aria-label={`Intuition Rating is calibrating, unlocking after GW${calibration.unlockGameweek} — tap to learn how this stat works`}
            >
              {/* Neutral glyph, never the Rookie sapling — that icon belongs
                  to a tier, and no tier is claimed yet. */}
              <SlidersHorizontal
                className="profile__skill-meter-tier-icon"
                aria-hidden="true"
              />
              <div>
                <p className="profile__skill-meter-tier-name">Calibrating</p>
                <p className="profile__skill-meter-calibrating-subline">
                  Rating unlocks after GW{calibration.unlockGameweek}
                </p>
              </div>
            </button>
            {/* Neither the correct-outcome % nor its week-on-week delta shows
                here: off one or two gameweeks both are the noise this state
                exists to hide, and a headline % reads as a rating the moment
                it's on screen. */}
          </div>

          <div
            className="profile__skill-meter-progress"
            role="img"
            aria-label={`Calibrating — ${calibration.progress} of ${CALIBRATION_GAMEWEEKS} gameweeks scored, rating unlocks after GW${calibration.unlockGameweek}`}
          >
            {calibration.gameweeks.map((gameweek, i) => {
              const isComplete = i < revealedProgress
              // Exactly one segment ever charges: the next incomplete one.
              const isCharging = i === revealedProgress
              return (
                <div
                  key={gameweek}
                  className="profile__skill-meter-progress-segment"
                >
                  <span
                    className={`profile__skill-meter-progress-track ${
                      isCharging
                        ? 'profile__skill-meter-progress-track--charging'
                        : ''
                    }`}
                  >
                    <span
                      className="profile__skill-meter-progress-fill"
                      style={{ transform: `scaleX(${isComplete ? 1 : 0})` }}
                    />
                  </span>
                  <span
                    className={`profile__skill-meter-progress-label ${
                      isComplete
                        ? 'profile__skill-meter-progress-label--complete'
                        : ''
                    }`}
                  >
                    GW{gameweek}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <>
          <div className="profile__skill-meter-headline">
            <button
              type="button"
              className="profile__skill-meter-tier"
              onClick={() => setIsInfoOpen(true)}
              aria-label={`${tier.name} tier — tap to learn how this stat and the skill tiers work`}
            >
              <TierIcon
                className="profile__skill-meter-tier-icon"
                aria-hidden="true"
              />
              <div>
                <p className="profile__skill-meter-tier-name">{tier.name}</p>
                {nextTier && (
                  <p className="profile__skill-meter-tier-next">
                    {Math.max(0, Math.ceil(nextTier.minPct - pct))}% to{' '}
                    {nextTier.name}
                  </p>
                )}
              </div>
            </button>
            <p className="profile__skill-meter-value">
              {formatPct(seasonStats.correctOutcomePct)}
            </p>
          </div>

          <div
            className="profile__skill-meter-bar"
            role="img"
            aria-label={`${formatPct(
              seasonStats.correctOutcomePct
            )} correct outcome, ${tier.name} tier`}
          >
            <div
              className="profile__skill-meter-fill"
              style={{
                width: `${isFillRevealed ? fillPct : 0}%`,
                transitionDuration: `${SKILL_METER_FILL_DURATION_MS}ms`,
              }}
            />
            <div
              className="profile__skill-meter-pin"
              style={{ left: `${baselineFillPct}%` }}
            >
              <span className="profile__skill-meter-pin-label">Home picks</span>
            </div>
          </div>

          {/* No tier scale under the bar — the headline above
              already names the tier, in the tier's own colour, next to its
              icon. The row restated it in 2xs type alongside three tiers the
              player isn't in. */}
        </>
      )}

      <div className="profile__skill-meter-divider" />

      <div className="profile__skill-meter-footer">
        <span>{seasonStats.correctPredictions} exact scores</span>
      </div>

      {isInfoOpen && (
        <SkillMeterInfoModal
          tiers={SKILL_TIERS}
          tierIndex={tierIndex}
          pct={pct}
          isCalibrating={showCalibrating}
          onClose={() => setIsInfoOpen(false)}
        />
      )}
    </div>
  )
}

export default SkillMeter
